import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { ApiService } from '@core/providers/http';

@Injectable({
  providedIn: 'root',
})
export class EscalaService {
  public params = new HttpParams();

  private readonly endpoint = '/api/Escalas';

  constructor(private readonly apiService: ApiService) {}

  // =========================================================
  // LISTAR ESCALAS
  // GET /api/Escalas
  // =========================================================

  list(params?: any): Observable<any> {
    return this.apiService
      .get(
        this.endpoint,
        params
          ? new HttpParams({
              fromObject: params,
            })
          : this.params,
      )
      .pipe(map((response) => response));
  }

  // =========================================================
  // CRIAR ESCALA
  // POST /api/Escalas
  // =========================================================

  store(escala: any): Observable<any> {
    return this.apiService.post(this.endpoint, escala);
  }

  // =========================================================
  // OBTER ESCALA
  // GET /api/Escalas/{id}
  // =========================================================

  show(id: string | number): Observable<any> {
    return this.apiService
      .get(`${this.endpoint}/${id}`)
      .pipe(map((response) => response));
  }

  getHistorico(id: string | number): Observable<any> {
    return this.apiService
      .get(`${this.endpoint}/${id}/historico`)
      .pipe(map((response) => response));
  }

  // =========================================================
  // ELIMINAR ESCALA
  // DELETE /api/Escalas/{id}
  // =========================================================

  delete(id: string | number): Observable<any> {
    return this.apiService.delete(`${this.endpoint}/${id}`);
  }

  // =========================================================
  // ATUALIZAR ESCALA
  // PUT /api/Escalas/{id}
  // =========================================================

  update(escala: any, id: string | number): Observable<any> {
    return this.apiService.put(`${this.endpoint}/${id}`, escala);
  }

  // =========================================================
  // ESTATÍSTICAS
  // GET /api/Escalas/{escalaId}/statistics
  // =========================================================

  statistics(escalaId: string | number): Observable<any> {
    return this.apiService
      .get(`${this.endpoint}/${escalaId}/statistics`)
      .pipe(map((response) => response));
  }

  // =========================================================
  // CRIAÇÃO EM MASSA
  // POST /api/Escalas/bulk
  // =========================================================

  bulk(escala: any): Observable<any> {
    return this.apiService.post(`${this.endpoint}/bulk`, escala);
  }

  // =========================================================
  // ESCALA POR EFETIVO E ESCALA
  // GET /api/Escalas/por-efetivo/{idEfetivo}/{escalaId}
  // =========================================================

  porEfetivoEscala(
    idEfetivo: string | number,
    escalaId: string | number,
  ): Observable<any> {
    return this.apiService
      .get(`${this.endpoint}/por-efetivo/${idEfetivo}/${escalaId}`)
      .pipe(map((response) => response));
  }

  // =========================================================
  // ESCALAS POR EFETIVO
  // GET /api/Escalas/por-efetivo/{idEfetivo}
  // =========================================================

  porEfetivo(idEfetivo: string | number): Observable<any> {
    return this.apiService
      .get(`${this.endpoint}/por-efetivo/${idEfetivo}`)
      .pipe(map((response) => response));
  }

  // =========================================================
  // ESCALAS POR POSTO
  // GET /api/Escalas/por-posto/{idPosto}
  // =========================================================

  porPosto(idPosto: string | number): Observable<any> {
    return this.apiService
      .get(`${this.endpoint}/por-posto/${idPosto}`)
      .pipe(map((response) => response));
  }

  // =========================================================
  // ESCALAS POR PERÍODO
  // GET /api/Escalas/por-periodo
  // =========================================================

  porPeriodo(params?: any): Observable<any> {
    return this.apiService
      .get(
        `${this.endpoint}/por-periodo`,
        params
          ? new HttpParams({
              fromObject: params,
            })
          : this.params,
      )
      .pipe(map((response) => response));
  }

  // =========================================================
  // ADICIONAR EFETIVO À ESCALA
  // PUT /api/Escalas/{escalaId}/{efetivoId}/AdicionarEfetivo
  // =========================================================

  adicionarEfetivo(
    escalaId: string | number,
    efetivoId: string | number,
  ): Observable<any> {
    return this.apiService.put(
      `${this.endpoint}/${escalaId}/${efetivoId}/AdicionarEfetivo`,
      {},
    );
  }

  // =========================================================
  // REMOVER EFETIVO DA ESCALA
  // DELETE /api/Escalas/{escalaId}/{efetivoId}/RemoverEfetivo
  // =========================================================

  removerEfetivo(
    escalaId: string | number,
    efetivoId: string | number,
  ): Observable<any> {
    return this.apiService.delete(
      `${this.endpoint}/${escalaId}/${efetivoId}/RemoverEfetivo`,
    );
  }

  // =========================================================
  // ADICIONAR EQUIPAMENTO AO EFETIVO
  // PUT /api/Escalas/{escalaEfetivoId}/{equipamentoId}/AdicionarEquipamentoAoEfetivo
  // =========================================================

  adicionarEquipamentoAoEfetivo(
    escalaEfetivoId: string | number,
    equipamentoId: string | number,
  ): Observable<any> {
    return this.apiService.put(
      `${this.endpoint}/${escalaEfetivoId}/${equipamentoId}/AdicionarEquipamentoAoEfetivo`,
      {},
    );
  }

  // =========================================================
  // REMOVER EQUIPAMENTO DO EFETIVO
  // DELETE /api/Escalas/{escalaEfetivoId}/{equipamentoId}/RemoverEquipamentoDoEfetivoAsync
  // =========================================================

  removerEquipamentoDoEfetivo(
    escalaEfetivoId: string | number,
    equipamentoId: string | number,
  ): Observable<any> {
    return this.apiService.delete(
      `${this.endpoint}/${escalaEfetivoId}/${equipamentoId}/RemoverEquipamentoDoEfetivoAsync`,
    );
  }

  // =========================================================
  // INICIAR ESCALA
  // POST /api/Escalas/{id}/{efetivoId}/iniciar
  // =========================================================

  iniciar(id: string | number, efetivoId: string | number): Observable<any> {
    return this.apiService.post(
      `${this.endpoint}/${id}/${efetivoId}/iniciar`,
      {},
    );
  }

  // =========================================================
  // CONCLUIR ESCALA
  // POST /api/Escalas/{id}/{efetivoId}/concluir
  // =========================================================

  concluir(id: string | number, efetivoId: string | number): Observable<any> {
    return this.apiService.post(
      `${this.endpoint}/${id}/${efetivoId}/concluir`,
      {},
    );
  }

  // =========================================================
  // ESCALAS A RECEBER POR POSTO
  // GET /api/Escalas/{idPosto}/receber
  // =========================================================

  receber(idPosto: string | number): Observable<any> {
    return this.apiService
      .get(`${this.endpoint}/${idPosto}/receber`)
      .pipe(map((response) => response));
  }

  // =========================================================
  // VERIFICAR EQUIPAMENTOS
  // POST /api/Escalas/{id}/verificar-equipamentos
  // =========================================================

  verificarEquipamentos(id: string | number): Observable<any> {
    return this.apiService.post(
      `${this.endpoint}/${id}/verificar-equipamentos`,
      {},
    );
  }

  // =========================================================
  // ESCALA ATUAL DO EFETIVO
  // GET /api/Escalas/efetivo/{efetivoId}/atual
  // =========================================================

  efetivoAtual(efetivoId: string | number): Observable<any> {
    return this.apiService
      .get(`${this.endpoint}/efetivo/${efetivoId}/atual`)
      .pipe(map((response) => response));
  }

  // =========================================================
  // REMOVER EFETIVO DA ESCALA
  // DELETE /api/Escalas/{id}/RemoverEfetivoEscala
  // =========================================================

  removerEfetivoEscala(id: string | number): Observable<any> {
    return this.apiService.delete(
      `${this.endpoint}/${id}/RemoverEfetivoEscala`,
    );
  }
}
